import * as yup from 'yup';

import { cloneDeep } from '@apollo/client/utilities';

import {
  AccessControlServiceDefinitions,
  AccessControlServiceEntity,
} from 'lane-shared/helpers/integrations/AccessManagement/accessControl';
import {
  IdentityProviderDefinitions,
  IdentityProviderEntity,
  HIDSharedProperties,
  HIDVersionProperty,
} from 'lane-shared/helpers/integrations/AccessManagement/identityProvider';
import { createShapeFromProperties } from 'lane-shared/properties';

/**
 * Utility function to delete specified fields from an object.
 * @param obj - The object from which fields should be deleted.
 * @param fields - An array of field keys to be deleted from the object.
 */
function deleteFields<T extends object>(
  obj: T,
  fields: (keyof T)[]
): Partial<T> {
  fields?.forEach(field => {
    delete obj[field];
  });

  return obj;
}

const ccureValidateNestedConfig = (
  settings: any,
  acsDefinition: any,
  credentialRangeSchema: any,
  credentialRangeSchemaDefinition: any,
  accessControlGroupsSchema: any,
  accessControlGroupsSchemaDefinition: any,
  personnelTypeValidationSchema: any
) => {
  const {
    partitionIdEnabled,
    mobileAccessUdf,
    sendInviteEmailUdf,
    visitorManagementEnabled,
    visitorFacilityCodeEnabled,
  } = settings.accessControlSettings || {};

  const acsDefinitionFieldsToRemove: Array<keyof typeof acsDefinition> = [];
  const settingsFieldsToRemove: Array<
    keyof typeof settings.accessControlSettings
  > = [];

  if (!partitionIdEnabled) {
    acsDefinitionFieldsToRemove.push('partitionId');
  }

  if (!mobileAccessUdf) {
    acsDefinitionFieldsToRemove.push('mobileAccessUdf');
  }

  if (!sendInviteEmailUdf) {
    acsDefinitionFieldsToRemove.push('sendInviteEmailUdf');
  }

  if (!visitorFacilityCodeEnabled) {
    acsDefinitionFieldsToRemove.push('visitorFacilityCode');
  }

  // Might need to delete the field for yup validation in MultiSelect
  if (!settings.accessControlSettings?.userPersonnelTypes?.length) {
    settingsFieldsToRemove.push('userPersonnelTypes');
  }

  if (!visitorManagementEnabled) {
    acsDefinitionFieldsToRemove.push('visitorCardFormat');
    acsDefinitionFieldsToRemove.push('visitorFacilityCode'); // Remove field for skip validation if VM off and VM facility code toggle on
    acsDefinitionFieldsToRemove.push('visitorPersonnelType');
  } else {
    let personnelTypeValidationSchemaDefinition;

    if (
      settings.accessControlSettings?.visitorPersonnelType?.value &&
      settings.accessControlSettings?.userPersonnelTypes?.length
    ) {
      personnelTypeValidationSchemaDefinition = yup
        .string()
        .test(
          'visitor-personnel-type-exists-in-user-personnel-types',
          'web.admin.channel.integrations.access.access-control-settings.same-personnel-type.error',
          function () {
            const settings =
              (this?.options?.context as { settings: any })?.settings || {}; // Access settings from context

            const visitorPersonnelTypeValue =
              settings?.accessControlSettings?.visitorPersonnelType?.value;
            const userPersonnelTypes =
              settings?.accessControlSettings?.userPersonnelTypes;

            return !userPersonnelTypes
              ?.map((item: { value: string }) => item.value)
              ?.includes(visitorPersonnelTypeValue);
          }
        );
    }

    credentialRangeSchema = credentialRangeSchemaDefinition;
    accessControlGroupsSchema = accessControlGroupsSchemaDefinition;
    personnelTypeValidationSchema = personnelTypeValidationSchemaDefinition;
  }

  deleteFields(settings.accessControlSettings, settingsFieldsToRemove);

  // Remove fields from acsDefinition
  deleteFields(acsDefinition, acsDefinitionFieldsToRemove);

  return {
    credentialRangeSchema,
    accessControlGroupsSchema,
    personnelTypeValidationSchema,
  };
};

const amagValidateNestedConfig = (
  settings: any,
  acsDefinition: any,
  credentialRangeSchema: any,
  credentialRangeSchemaDefinition: any
) => {
  const { visitorManagementEnabled, visitorCustomerCodeEnabled } =
    settings.accessControlSettings || {};

  if (visitorManagementEnabled) {
    credentialRangeSchema = credentialRangeSchemaDefinition;
  }

  if (visitorCustomerCodeEnabled !== true) {
    delete acsDefinition.visitorCustomerCode;
    delete acsDefinition.visitorCustomerCodeEnabled;
  }

  return { credentialRangeSchema };
};

const braxosValidateNestedConfig = (
  settings: any,
  acsDefinition: any,
  credentialRangeSchema: any,
  credentialRangeSchemaDefinition: any
) => {
  const { cardFormat, visitorCardFormat, visitorManagementEnabled } =
    settings.accessControlSettings || {};

  if (Object.keys(cardFormat || {}).length === 0) {
    delete acsDefinition.cardFormat;
  }

  if (Object.keys(visitorCardFormat || {}).length === 0) {
    delete acsDefinition.visitorCardFormat;
  }

  if (visitorManagementEnabled) {
    credentialRangeSchema = credentialRangeSchemaDefinition;
  }

  return { credentialRangeSchema };
};

const kastleValidateNestedConfig = (settings: any, acsDefinition: any) => {
  const { visitorManagementEnabled, accessEnabled } =
    settings.accessControlSettings || {};

  if (!accessEnabled) {
    const accessFieldsToRemove: Array<keyof typeof acsDefinition> = [
      'username',
      'password',
      'ownerGuid',
      'scimApiKey',
      'facilityCode',
      'cardFormat',
    ];

    deleteFields(acsDefinition, accessFieldsToRemove);
  }

  if (!visitorManagementEnabled) {
    const acsDefinitionFieldsToRemove: Array<keyof typeof acsDefinition> = [
      'buildingId',
      'visitorConnectOwnerGuid',
      'visitorConnectPassword',
      'visitorConnectUsername',
    ];

    // Remove fields from acsDefinition
    deleteFields(acsDefinition, acsDefinitionFieldsToRemove);
  }
};

const geneaValidateNestedConfig = (
  settings: any,
  acsDefinition: any,
  credentialRangeSchema: any,
  credentialRangeSchemaDefinition: any,
  accessControlGroupsSchema: any,
  accessControlGroupsSchemaDefinition: any
) => {
  const { locationUdfEnabled, visitorManagementEnabled, visitorCardFormat } =
    settings.accessControlSettings || {};

  const acsDefinitionFieldsToRemove: Array<keyof typeof acsDefinition> = [];

  if (!locationUdfEnabled) {
    acsDefinitionFieldsToRemove.push('locationUdf');
  }

  // skip validation of visitorCardFormat
  if (
    Object.keys(visitorCardFormat || {}).length === 0 ||
    !visitorManagementEnabled
  ) {
    acsDefinitionFieldsToRemove.push('visitorCardFormat');
  }

  if (visitorManagementEnabled) {
    credentialRangeSchema = credentialRangeSchemaDefinition;
    accessControlGroupsSchema = accessControlGroupsSchemaDefinition;
  }

  deleteFields(acsDefinition, acsDefinitionFieldsToRemove);

  return { credentialRangeSchema, accessControlGroupsSchema };
};

const genetecValidateNestedConfig = (
  settings: any,
  acsDefinition: any,
  credentialRangeSchema: any,
  credentialRangeSchemaDefinition: any,
  accessControlGroupsSchema: any,
  accessControlGroupsSchemaDefinition: any
) => {
  const { visitorManagementEnabled } = settings.accessControlSettings || {};

  const acsDefinitionFieldsToRemove: Array<keyof typeof acsDefinition> = [];

  if (
    Object.keys(settings.accessControlSettings?.mobAccessUdf || {}).length === 0
  ) {
    acsDefinitionFieldsToRemove.push('mobAccessUdf');
  }

  if (!settings.accessControlSettings?.cardholderGroupsFilterEnabled) {
    acsDefinitionFieldsToRemove.push('cardholderGroups');
  }

  if (visitorManagementEnabled) {
    credentialRangeSchema = credentialRangeSchemaDefinition;
    accessControlGroupsSchema = accessControlGroupsSchemaDefinition;
  } else {
    acsDefinitionFieldsToRemove.push('visitorCardFormat');
  }

  deleteFields(acsDefinition, acsDefinitionFieldsToRemove);

  return { credentialRangeSchema, accessControlGroupsSchema };
};

const sipassValidateNestedConfig = (
  settings: any,
  acsDefinition: any,
  credentialRangeSchema: any,
  credentialRangeSchemaDefinition: any,
  accessControlGroupsSchema: any,
  accessControlGroupsSchemaDefinition: any
) => {
  const { visitorManagementEnabled } = settings.accessControlSettings || {};

  const settingsFieldsToRemove: Array<
    keyof typeof settings.accessControlSettings
  > = [];

  // Might need to delete the field for yup validation in MultiSelect
  if (!settings.accessControlSettings?.visitorCardFormats?.length) {
    settingsFieldsToRemove.push('visitorCardFormats');
  }

  if (!settings.accessControlSettings?.cardFormats?.length) {
    settingsFieldsToRemove.push('cardFormats');
  }

  deleteFields(settings.accessControlSettings, settingsFieldsToRemove);

  if (!visitorManagementEnabled) {
    const acsDefinitionFieldsToRemove: Array<keyof typeof acsDefinition> = [
      'visitorCardFormats',
    ];

    deleteFields(acsDefinition, acsDefinitionFieldsToRemove); // skip visitor card format validation if visitorManagementEnabled toggle is off
  } else {
    credentialRangeSchema = credentialRangeSchemaDefinition;
    accessControlGroupsSchema = accessControlGroupsSchemaDefinition;
  }

  return { credentialRangeSchema, accessControlGroupsSchema };
};

export const validateNestedConfig = (settings: any) => {
  let credentialRangeSchema;
  let accessControlGroupsSchema;
  let personnelTypeValidationSchema;

  const credentialRangeSchemaDefinition = yup.object().shape({
    min: yup
      .string()
      .required(
        'web.admin.channel.integrations.access.access-control-settings.visitor-management.credential-range.min.error'
      ),
    max: yup
      .string()
      .required(
        'web.admin.channel.integrations.access.access-control-settings.visitor-management.credential-range.max.error'
      )
      .test(
        'is-greater',
        'web.admin.channel.integrations.access.access-control-settings.visitor-management.credential-range.max-greater-min.error',
        function (value) {
          const min = this.parent.min;

          if (!min) return true;

          return parseInt(<string>value, 10) > parseInt(min, 10); // Check if max is greater than min
        }
      ),
  });
  const accessControlGroupsSchemaDefinition = yup.lazy(
    (value: any[] | undefined) => {
      if (!value || value?.length === 0) {
        return yup
          .array()
          .nullable()
          .required(
            'web.admin.channel.integrations.access.access-control-settings.visitor-management.access-control-mapping.access-control-group.error'
          );
      }

      return yup.array().of(
        yup.object().shape({
          accessGroupIDs: yup
            .array()
            .min(
              1,
              'web.admin.channel.integrations.access.access-control-settings.visitor-management.access-control-mapping.access-control-groups.one.error'
            ),
          vtsTenantIDs: yup
            .array()
            .min(
              1,
              'web.admin.channel.integrations.access.access-control-settings.visitor-management.access-control-mapping.apply-to-these-tenants.error'
            ),
          geneaTenantIDs: yup
            .array()
            .min(
              1,
              'web.admin.channel.integrations.access.access-control-settings.visitor-management.access-control-mapping.apply-to-these-tenants.error'
            ),
        })
      );
    }
  );

  let idpDefinition = cloneDeep(
    IdentityProviderDefinitions[
      settings.identityProviderService?.value as IdentityProviderEntity
    ]
  );
  const acsDefinition = cloneDeep(
    AccessControlServiceDefinitions[
      settings.accessControlService?.value as AccessControlServiceEntity
    ]
  );

  if (
    settings.identityProviderSettings &&
    'isAppleWalletEnabled' in settings.identityProviderSettings &&
    !settings.identityProviderSettings.isAppleWalletEnabled
  ) {
    idpDefinition = { ...HIDSharedProperties, ...HIDVersionProperty };
  }

  if (
    settings.accessControlService?.value ===
      AccessControlServiceEntity.SaltoSvn &&
    settings.identityProviderService?.value === IdentityProviderEntity.SaltoSvn
  ) {
    if (
      'partitionIdEnabled' in settings.accessControlSettings &&
      !settings.accessControlSettings.partitionIdEnabled
    ) {
      delete acsDefinition.partitionId;
      delete idpDefinition.partitionId;
    }
  }

  if (!idpDefinition || !acsDefinition) return;

  const idpValidation = yup
    .object()
    .shape(createShapeFromProperties(idpDefinition));

  idpValidation.validateSync(settings.identityProviderSettings, {
    abortEarly: false,
  });

  if (
    settings.accessControlService?.value === AccessControlServiceEntity.CCure
  ) {
    const {
      credentialRangeSchema: updatedCredentialRangeSchema,
      accessControlGroupsSchema: updatedAccessControlGroupsSchema,
      personnelTypeValidationSchema: updatedPersonnelTypeValidationSchema,
    } = ccureValidateNestedConfig(
      settings,
      acsDefinition,
      credentialRangeSchema,
      credentialRangeSchemaDefinition,
      accessControlGroupsSchema,
      accessControlGroupsSchemaDefinition,
      personnelTypeValidationSchema
    );

    credentialRangeSchema = updatedCredentialRangeSchema;
    accessControlGroupsSchema = updatedAccessControlGroupsSchema;
    personnelTypeValidationSchema = updatedPersonnelTypeValidationSchema;
  }

  if (
    settings.accessControlService?.value === AccessControlServiceEntity.Amag
  ) {
    const { credentialRangeSchema: updatedCredentialRangeSchema } =
      amagValidateNestedConfig(
        settings,
        acsDefinition,
        credentialRangeSchema,
        credentialRangeSchemaDefinition
      );

    credentialRangeSchema = updatedCredentialRangeSchema;
  }

  if (
    settings.accessControlService?.value === AccessControlServiceEntity.Genetec
  ) {
    const {
      credentialRangeSchema: updatedCredentialRangeSchema,
      accessControlGroupsSchema: updatedAccessControlGroupsSchema,
    } = genetecValidateNestedConfig(
      settings,
      acsDefinition,
      credentialRangeSchema,
      credentialRangeSchemaDefinition,
      accessControlGroupsSchema,
      accessControlGroupsSchemaDefinition
    );

    credentialRangeSchema = updatedCredentialRangeSchema;
    accessControlGroupsSchema = updatedAccessControlGroupsSchema;
  }

  if (
    settings.accessControlService?.value === AccessControlServiceEntity.Sipass
  ) {
    const {
      credentialRangeSchema: updatedCredentialRangeSchema,
      accessControlGroupsSchema: updatedAccessControlGroupsSchema,
    } = sipassValidateNestedConfig(
      settings,
      acsDefinition,
      credentialRangeSchema,
      credentialRangeSchemaDefinition,
      accessControlGroupsSchema,
      accessControlGroupsSchemaDefinition
    );

    credentialRangeSchema = updatedCredentialRangeSchema;
    accessControlGroupsSchema = updatedAccessControlGroupsSchema;
  }

  if (
    settings.accessControlService?.value === AccessControlServiceEntity.Maxxess
  ) {
    const { emailType } = settings.accessControlSettings || {};

    if (emailType?.value === 'mobileEmail') {
      delete acsDefinition.userEmailField;
    }
  }

  if (
    settings.accessControlService?.value === AccessControlServiceEntity.Genea
  ) {
    const {
      credentialRangeSchema: updatedCredentialRangeSchema,
      accessControlGroupsSchema: updatedAccessControlGroupsSchema,
    } = geneaValidateNestedConfig(
      settings,
      acsDefinition,
      credentialRangeSchema,
      credentialRangeSchemaDefinition,
      accessControlGroupsSchema,
      accessControlGroupsSchemaDefinition
    );

    credentialRangeSchema = updatedCredentialRangeSchema;
    accessControlGroupsSchema = updatedAccessControlGroupsSchema;
  }

  if (
    settings.accessControlService?.value === AccessControlServiceEntity.Kastle
  ) {
    kastleValidateNestedConfig(settings, acsDefinition);
  }

  if (
    settings.accessControlService?.value ===
    AccessControlServiceEntity.Gallagher
  ) {
    if (!('division' in settings.accessControlSettings)) {
      delete acsDefinition.division;
    }
  }

  if (
    settings.accessControlService?.value === AccessControlServiceEntity.Braxos
  ) {
    const { credentialRangeSchema: updatedCredentialRangeSchema } =
      braxosValidateNestedConfig(
        settings,
        acsDefinition,
        credentialRangeSchema,
        credentialRangeSchemaDefinition
      );

    credentialRangeSchema = updatedCredentialRangeSchema;
  }

  const acsValidation = yup.object().shape({
    ...createShapeFromProperties(acsDefinition),
    ...(credentialRangeSchema && {
      visitorCredentialRange: credentialRangeSchema,
    }), // Include visitorCredentialRange schema conditionally
    ...(accessControlGroupsSchema && {
      visitorAccessGroupMappings: accessControlGroupsSchema,
    }), // Include visitorAccessGroupMappings schema conditionally
    ...(personnelTypeValidationSchema && {
      userPersonnelTypes: personnelTypeValidationSchema,
    }),
  });

  acsValidation.validateSync(settings.accessControlSettings, {
    abortEarly: false,
    context: { settings },
  });
};
